import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, DollarSignIcon, BuildingIcon, CalendarIcon, FileTextIcon, CheckCircleIcon } from 'lucide-react';
export function BillFinancingWizard() {
  const {
    t
  } = useTranslation('bills');
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    loanPackage: '',
    financingAmount: '',
    requestedTerm: '30',
    purpose: '',
    additionalNotes: ''
  });
  // Mock bill data
  const bill = {
    id: id,
    billNumber: 'BILL-2024-002',
    supplierName: 'Spice Traders Co',
    amount: 87500,
    dueDate: '2024-02-20'
  };
  const dfccLoanPackages = [{
    id: 'standard',
    name: 'Standard Financing',
    interestRate: '12%',
    maxTerm: '90 days',
    processingTime: '48 hours',
    description: 'Ideal for regular business operations with flexible terms'
  }, {
    id: 'premium',
    name: 'Premium Financing',
    interestRate: '10.5%',
    maxTerm: '120 days',
    processingTime: '24 hours',
    description: 'Lower rates for established businesses with priority processing'
  }, {
    id: 'express',
    name: 'Express Financing',
    interestRate: '14%',
    maxTerm: '60 days',
    processingTime: '12 hours',
    description: 'Quick approval for urgent cash flow needs'
  }, {
    id: 'sme',
    name: 'SME Growth Package',
    interestRate: '11%',
    maxTerm: '90 days',
    processingTime: '36 hours',
    description: 'Special rates for small and medium enterprises'
  }];
  const handleSubmit = () => {
    console.log('Submitting financing request:', {
      billId: id,
      ...formData
    });
    navigate('/bills');
  };
  const selectedPackage = dfccLoanPackages.find(p => p.id === formData.loanPackage);
  return <div className="max-w-4xl mx-auto space-y-6" data-id="element-3695">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-3696">
        <button onClick={() => navigate(`/bill-detail/${id}`)} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-3697">
          <ArrowLeftIcon size={20} data-id="element-3698" />
          {t('pages.BillFinancingWizard.back_to_bill')}
        </button>
      </div>
      {/* Progress Steps */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-3699">
        <div className="flex items-center justify-between mb-8" data-id="element-3700">
          <div className="flex-1" data-id="element-3701">
            <div className={`flex items-center ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-3702">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-3703">
                1
              </div>
              <div className="ml-3" data-id="element-3704">
                <div className="text-sm font-medium" data-id="element-3705">
                  {t('pages.BillApprovalWizard.bill_details')}
                </div>
              </div>
            </div>
          </div>
          <div className={`flex-1 h-1 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-3706"></div>
          <div className="flex-1" data-id="element-3707">
            <div className={`flex items-center ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-3708">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-3709">
                2
              </div>
              <div className="ml-3" data-id="element-3710">
                <div className="text-sm font-medium" data-id="element-3711">
                  {t('pages.BillFinancingWizard.select_provider')}
                </div>
              </div>
            </div>
          </div>
          <div className={`flex-1 h-1 ${step >= 3 ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-3712"></div>
          <div className="flex-1" data-id="element-3713">
            <div className={`flex items-center ${step >= 3 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-3714">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-3715">
                3
              </div>
              <div className="ml-3" data-id="element-3716">
                <div className="text-sm font-medium" data-id="element-3717">
                  {t('pages.BillFinancingWizard.financing_terms')}
                </div>
              </div>
            </div>
          </div>
          <div className={`flex-1 h-1 ${step >= 4 ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-3718"></div>
          <div className="flex-1" data-id="element-3719">
            <div className={`flex items-center ${step >= 4 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-3720">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 4 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-3721">
                4
              </div>
              <div className="ml-3" data-id="element-3722">
                <div className="text-sm font-medium" data-id="element-3723">
                  {t('pages.BillFinancingWizard.review_submit')}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Step 1: Bill Details */}
        {step === 1 && <div className="space-y-6" data-id="element-3724">
            <div data-id="element-3725">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-3726">
                {t('pages.BillFinancingWizard.bill_information')}
              </h2>
              <p className="text-gray-600" data-id="element-3727">
                {t('pages.BillFinancingWizard.review_the_bill_details_for_financing')}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 space-y-4" data-id="element-3728">
              <div className="grid grid-cols-2 gap-4" data-id="element-3729">
                <div data-id="element-3730">
                  <div className="text-sm text-gray-500" data-id="element-3731">
                    {t('pages.BillFinancingWizard.bill_number')}
                  </div>
                  <div className="text-base font-medium text-gray-900" data-id="element-3732">
                    {bill.billNumber}
                  </div>
                </div>
                <div data-id="element-3733">
                  <div className="text-sm text-gray-500" data-id="element-3734">
                    {t('pages.BillFinancingWizard.supplier')}
                  </div>
                  <div className="text-base font-medium text-gray-900" data-id="element-3735">
                    {bill.supplierName}
                  </div>
                </div>
                <div data-id="element-3736">
                  <div className="text-sm text-gray-500" data-id="element-3737">
                    {t('pages.BillDetail.due_date')}
                  </div>
                  <div className="text-base font-medium text-gray-900" data-id="element-3738">
                    {new Date(bill.dueDate).toLocaleDateString('en-GB')}
                  </div>
                </div>
                <div data-id="element-3739">
                  <div className="text-sm text-gray-500" data-id="element-3740">
                    {t('pages.BillFinancingWizard.bill_amount')}
                  </div>
                  <div className="text-2xl font-bold text-gray-900" data-id="element-3741">
                    {bill.currency} {bill.amount.toLocaleString('en-US')}
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-3742">
              <div className="flex items-start gap-3" data-id="element-3743">
                <DollarSignIcon size={20} className="text-blue-600 mt-0.5" data-id="element-3744" />
                <div className="text-sm text-blue-800" data-id="element-3745">
                  <div className="font-medium mb-1" data-id="element-3746">
                    {t('pages.BillFinancingWizard.bill_financing_benefits')}
                  </div>
                  <ul className="list-disc list-inside space-y-1" data-id="element-3747">
                    <li data-id="element-3748">
                      {t('pages.BillFinancingWizard.improve_cash_flow_by_paying_suppliers')}
                    </li>
                    <li data-id="element-3749">
                      {t('pages.BillFinancingWizard.maintain_good_supplier_relationships')}
                    </li>
                    <li data-id="element-3750">
                      {t('pages.BillFinancingWizard.take_advantage_of_early_payment_discounts')}
                    </li>
                    <li data-id="element-3751">
                      {t('pages.BillFinancingWizard.flexible_repayment_terms')}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="flex justify-end" data-id="element-3752">
              <button onClick={() => setStep(2)} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-id="element-3753">
                {t('pages.BillFinancingWizard.continue')}
              </button>
            </div>
          </div>}
        {/* Step 2: Select Provider */}
        {step === 2 && <div className="space-y-6" data-id="element-3754">
            <div data-id="element-3755">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-3756">
                {t('pages.BillFinancingWizard.select_pomelo_financing_package')}
              </h2>
              <p className="text-gray-600" data-id="element-3757">
                {t('pages.BillFinancingWizard.choose_a_financing_package_that_best')}
              </p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6" data-id="element-3758">
              <div className="flex items-start gap-3" data-id="element-3759">
                <BuildingIcon size={20} className="text-blue-600 mt-0.5" data-id="element-3760" />
                <div data-id="element-3761">
                  <div className="font-semibold text-blue-900 mb-1" data-id="element-3762">
                    {t('pages.BillFinancingWizard.pomelo_your_trusted_financial_partner')}
                  </div>
                  <div className="text-sm text-blue-800" data-id="element-3763">
                    {t('pages.BillFinancingWizard.as_a_valued_pomelo_customer_you')}
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-4" data-id="element-3764">
              {dfccLoanPackages.map(pkg => <label key={pkg.id} className={`block p-6 border-2 rounded-lg cursor-pointer transition-all ${formData.loanPackage === pkg.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`} data-id="element-3765">
                  <input type="radio" name="loanPackage" value={pkg.id} checked={formData.loanPackage === pkg.id} onChange={e => setFormData({
              ...formData,
              loanPackage: e.target.value
            })} className="sr-only" data-id="element-3766" />

                  <div className="flex items-start justify-between" data-id="element-3767">
                    <div className="flex-1" data-id="element-3768">
                      <div className="flex items-center gap-2 mb-2" data-id="element-3769">
                        <div className="text-lg font-semibold text-gray-900" data-id="element-3770">
                          {pkg.name}
                        </div>
                        {formData.loanPackage === pkg.id && <CheckCircleIcon size={20} className="text-blue-600" data-id="element-3771" />}
                      </div>
                      <p className="text-sm text-gray-600 mb-4" data-id="element-3772">
                        {pkg.description}
                      </p>
                      <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-3773">
                        <div data-id="element-3774">
                          <div className="text-gray-500" data-id="element-3775">
                            {t('pages.BillFinancingWizard.interest_rate')}
                          </div>
                          <div className="font-medium text-gray-900" data-id="element-3776">
                            {pkg.interestRate}
                          </div>
                        </div>
                        <div data-id="element-3777">
                          <div className="text-gray-500" data-id="element-3778">
                            {t('pages.BillFinancingWizard.max_term')}
                          </div>
                          <div className="font-medium text-gray-900" data-id="element-3779">
                            {pkg.maxTerm}
                          </div>
                        </div>
                        <div data-id="element-3780">
                          <div className="text-gray-500" data-id="element-3781">
                            {t('pages.BillFinancingWizard.processing_time')}
                          </div>
                          <div className="font-medium text-gray-900" data-id="element-3782">
                            {pkg.processingTime}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </label>)}
            </div>
            <div className="flex justify-between" data-id="element-3783">
              <button onClick={() => setStep(1)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-3784">
                {t('pages.BillApprovalWizard.back')}
              </button>
              <button onClick={() => setStep(3)} disabled={!formData.loanPackage} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3785">
                {t('pages.BillFinancingWizard.continue')}
              </button>
            </div>
          </div>}
        {/* Step 3: Financing Terms */}
        {step === 3 && <div className="space-y-6" data-id="element-3786">
            <div data-id="element-3787">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-3788">
                {t('pages.BillFinancingWizard.financing_terms')}
              </h2>
              <p className="text-gray-600" data-id="element-3789">
                {t('pages.BillFinancingWizard.specify_the_financing_amount_and_terms')}
              </p>
            </div>
            <div className="space-y-6" data-id="element-3790">
              <div data-id="element-3791">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3792">
                  {t('pages.BillFinancingWizard.financing_amount_usd')}
                </label>
                <input type="number" value={formData.financingAmount} onChange={e => setFormData({
              ...formData,
              financingAmount: e.target.value
            })} placeholder={t('pages.BillFinancingWizard.attrs.enter_amount')} max={bill.amount} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-3793" />

                <div className="mt-1 text-sm text-gray-500" data-id="element-3794">
                  {t('pages.BillFinancingWizard.maximum')}{' '}
                  {bill.amount.toLocaleString('en-US')}
                </div>
              </div>
              <div data-id="element-3795">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3796">
                  {t('pages.BillFinancingWizard.requested_term_days')}
                </label>
                <select value={formData.requestedTerm} onChange={e => setFormData({
              ...formData,
              requestedTerm: e.target.value
            })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-3797">
                  <option value="15" data-id="element-3798">
                    {t('pages.BillFinancingWizard.15_days')}
                  </option>
                  <option value="30" data-id="element-3799">
                    {t('pages.BillFinancingWizard.30_days')}
                  </option>
                  <option value="45" data-id="element-3800">
                    {t('pages.BillFinancingWizard.45_days')}
                  </option>
                  <option value="60" data-id="element-3801">
                    {t('pages.BillFinancingWizard.60_days')}
                  </option>
                  <option value="90" data-id="element-3802">
                    {t('pages.BillFinancingWizard.90_days')}
                  </option>
                </select>
              </div>
              <div data-id="element-3803">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3804">
                  {t('pages.BillFinancingWizard.purpose_of_financing')}
                </label>
                <select value={formData.purpose} onChange={e => setFormData({
              ...formData,
              purpose: e.target.value
            })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-3805">
                  <option value="" data-id="element-3806">
                    {t('pages.BillFinancingWizard.select_purpose')}
                  </option>
                  <option value="early_payment" data-id="element-3807">
                    {t('pages.BillFinancingWizard.early_payment_discount')}
                  </option>
                  <option value="cash_flow" data-id="element-3808">
                    {t('pages.BillFinancingWizard.cash_flow_management')}
                  </option>
                  <option value="supplier_relationship" data-id="element-3809">
                    {t('pages.BillFinancingWizard.supplier_relationship')}
                  </option>
                  <option value="other" data-id="element-3810">
                    {t('pages.BillFinancingWizard.other')}
                  </option>
                </select>
              </div>
              <div data-id="element-3811">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3812">
                  {t('pages.BillFinancingWizard.additional_notes')}
                </label>
                <textarea value={formData.additionalNotes} onChange={e => setFormData({
              ...formData,
              additionalNotes: e.target.value
            })} rows={4} placeholder={t('pages.BillFinancingWizard.attrs.any_additional_information')} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-3813" />
              </div>
            </div>
            <div className="flex justify-between" data-id="element-3814">
              <button onClick={() => setStep(2)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-3815">
                {t('pages.BillApprovalWizard.back')}
              </button>
              <button onClick={() => setStep(4)} disabled={!formData.financingAmount || !formData.requestedTerm || !formData.purpose} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3816">
                {t('pages.BillFinancingWizard.review_request')}
              </button>
            </div>
          </div>}
        {/* Step 4: Review & Submit */}
        {step === 4 && <div className="space-y-6" data-id="element-3817">
            <div data-id="element-3818">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-3819">
                {t('pages.BillFinancingWizard.review_financing_request')}
              </h2>
              <p className="text-gray-600" data-id="element-3820">
                {t('pages.BillFinancingWizard.please_review_your_financing_request_before')}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 space-y-6" data-id="element-3821">
              <div data-id="element-3822">
                <h3 className="text-sm font-medium text-gray-500 mb-3" data-id="element-3823">
                  {t('pages.BillFinancingWizard.bill_information_2')}
                </h3>
                <div className="grid grid-cols-2 gap-4" data-id="element-3824">
                  <div data-id="element-3825">
                    <div className="text-sm text-gray-500" data-id="element-3826">
                      {t('pages.BillFinancingWizard.bill_number')}
                    </div>
                    <div className="text-base font-medium text-gray-900" data-id="element-3827">
                      {bill.billNumber}
                    </div>
                  </div>
                  <div data-id="element-3828">
                    <div className="text-sm text-gray-500" data-id="element-3829">
                      {t('pages.BillFinancingWizard.supplier')}
                    </div>
                    <div className="text-base font-medium text-gray-900" data-id="element-3830">
                      {bill.supplierName}
                    </div>
                  </div>
                </div>
              </div>
              <div className="pt-4 border-t border-gray-200" data-id="element-3831">
                <h3 className="text-sm font-medium text-gray-500 mb-3" data-id="element-3832">
                  {t('pages.BillFinancingWizard.financing_details')}
                </h3>
                <div className="grid grid-cols-2 gap-4" data-id="element-3833">
                  <div data-id="element-3834">
                    <div className="text-sm text-gray-500" data-id="element-3835">
                      {t('pages.BillFinancingWizard.bank')}
                    </div>
                    <div className="text-base font-medium text-gray-900" data-id="element-3836">
                      {t('pages.BillFinancingWizard.pomelo')}
                    </div>
                  </div>
                  <div data-id="element-3837">
                    <div className="text-sm text-gray-500" data-id="element-3838">
                      {t('pages.BillFinancingWizard.package')}
                    </div>
                    <div className="text-base font-medium text-gray-900" data-id="element-3839">
                      {selectedPackage?.name}
                    </div>
                  </div>
                  <div data-id="element-3840">
                    <div className="text-sm text-gray-500" data-id="element-3841">
                      {t('pages.BillFinancingWizard.interest_rate')}
                    </div>
                    <div className="text-base font-medium text-gray-900" data-id="element-3842">
                      {selectedPackage?.interestRate}
                    </div>
                  </div>
                  <div data-id="element-3843">
                    <div className="text-sm text-gray-500" data-id="element-3844">
                      {t('pages.BillFinancingWizard.processing_time')}
                    </div>
                    <div className="text-base font-medium text-gray-900" data-id="element-3845">
                      {selectedPackage?.processingTime}
                    </div>
                  </div>
                  <div data-id="element-3846">
                    <div className="text-sm text-gray-500" data-id="element-3847">
                      {t('pages.BillFinancingWizard.financing_amount')}
                    </div>
                    <div className="text-base font-medium text-gray-900" data-id="element-3848">
                      {t('pages.BillFinancingWizard.rs')}{' '}
                      {parseFloat(formData.financingAmount).toLocaleString('en-US')}
                    </div>
                  </div>
                  <div data-id="element-3849">
                    <div className="text-sm text-gray-500" data-id="element-3850">
                      {t('pages.BillFinancingWizard.term')}
                    </div>
                    <div className="text-base font-medium text-gray-900" data-id="element-3851">
                      {formData.requestedTerm}{' '}
                      {t('pages.BillFinancingWizard.days')}
                    </div>
                  </div>
                  <div data-id="element-3852">
                    <div className="text-sm text-gray-500" data-id="element-3853">
                      {t('pages.BillFinancingWizard.purpose')}
                    </div>
                    <div className="text-base font-medium text-gray-900 capitalize" data-id="element-3854">
                      {formData.purpose.replace('_', ' ')}
                    </div>
                  </div>
                </div>
              </div>
              {formData.additionalNotes && <div className="pt-4 border-t border-gray-200" data-id="element-3855">
                  <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-3856">
                    {t('pages.BillFinancingWizard.additional_notes_2')}
                  </h3>
                  <p className="text-sm text-gray-700" data-id="element-3857">
                    {formData.additionalNotes}
                  </p>
                </div>}
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-3858">
              <div className="flex items-start gap-3" data-id="element-3859">
                <FileTextIcon size={20} className="text-blue-600 mt-0.5" data-id="element-3860" />
                <div className="text-sm text-blue-800" data-id="element-3861">
                  {t('pages.BillFinancingWizard.by_submitting_this_request_you_agree')}{' '}
                  {selectedPackage?.name}
                  {t('pages.BillFinancingWizard.the_request_will_be_processed_within')}{' '}
                  {selectedPackage?.processingTime}.
                </div>
              </div>
            </div>
            <div className="flex justify-between" data-id="element-3862">
              <button onClick={() => setStep(3)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-3863">
                {t('pages.BillApprovalWizard.back')}
              </button>
              <button onClick={handleSubmit} className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700" data-id="element-3864">
                {t('pages.BillFinancingWizard.submit_financing_request')}
              </button>
            </div>
          </div>}
      </div>
    </div>;
}